import React from 'react';
import { motion } from 'framer-motion';
import { Search, Upload, Download, Calendar, Users, Database, X } from 'lucide-react';

const ContactHeader = ({
  searchTerm,
  setSearchTerm,
  onUploadClick,
  onDownloadTemplate,
  totalContacts,
  totalGroups,
  selectedFilter,
  setSelectedFilter,
  filters = ['All Groups', 'Active', 'Recent'],
  dateFrom = '',
  dateTo = '',
  onDateFilterChange,
  onClearDateFilters,
}) => {
  const hasDateFilter = dateFrom || dateTo;

  const handleDateFromChange = (e) => {
    const from = e.target.value || '';
    onDateFilterChange?.(from, dateTo);
  };

  const handleDateToChange = (e) => {
    const to = e.target.value || '';
    onDateFilterChange?.(dateFrom, to);
  };
  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-4 sm:mb-6">
      {/* Header Title */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Contact Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Manage your contact groups and organize your messaging lists
          </p>
        </div>
        {/* Quick Stats */}
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-full">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">{totalContacts.toLocaleString()}</span>
            <span className="text-xs text-red-600 hidden sm:inline">contacts</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full">
            <Database className="w-4 h-4" />
            <span className="text-sm font-medium">{totalGroups}</span>
            <span className="text-xs text-purple-600 hidden sm:inline">groups</span>
          </div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col lg:flex-row gap-4 mb-4 sm:mb-6">
        {/* Search Bar */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input
            type="text"
            placeholder="Search contact groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={onDownloadTemplate}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium text-sm sm:text-base"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Template</span>
            <span className="sm:hidden">Download</span>
          </button>
          <button
            onClick={onUploadClick}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg sm:rounded-xl transition-colors duration-200 font-medium text-sm sm:text-base"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Upload Contacts</span>
            <span className="sm:hidden">Upload</span>
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {filters.map((filter) => (
          <button
            key={filter}
            onClick={() => setSelectedFilter(filter)}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
              selectedFilter === filter
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Date filters */}
      <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-gray-500" />
          <span className="font-medium">Date range:</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={handleDateFromChange}
            max={dateTo || undefined}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            aria-label="From date"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={handleDateToChange}
            min={dateFrom || undefined}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            aria-label="To date"
          />
          {hasDateFilter && onClearDateFilters && (
            <motion.button
              type="button"
              onClick={onClearDateFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <X className="w-4 h-4" />
              Clear dates
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactHeader; 